<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSimpleExistingPatientSelect from "@components/PatientSelect/SimpleExistingPatientSelect.vue";
  import ComponentSolveConflictingPatientData from "@components/PatientSelect/SolveConflictingPatientData.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {patientGender} from "@pages/Patients/Enums/Enums.js";
  import {
    MedButton,
    MedCheckbox,
    MedFormField,
    MedLabel,
    MedLink,
    MedSpinner,
    MedTooltip,
  } from "@pharma4u/medicheck-components";
  import filterExistingPatients from "@utils/Helpers/FilterExistingPatients.js";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentUniversalPatientAssignment",
  });

  const props = defineProps({
    /** Continue automatically when no direct match exists. */
    suppressWithoutMatch: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["existingPatientSelected", "newPatient", "hasMatch"]);

  const privacy = inject("$privacy");

  const refSolveConflictingPatientData = ref(null);

  const isVisible = ref(false);
  const isLoading = ref(false);
  const existingPatients = ref([]);
  const looseSearch = ref(false);
  const looseSearchIncludedFields = ref({
    name: true,
    birthdate: false,
    gender: false,
    insurancenumber: false,
  });

  const status = {
    SEARCH: "search", // search for decryptedPatient
    MISSING_DATA: "missing-data", // decryptedPatient is incomplete
    EDIT_DATA: "edit-data", // user decided to edit decryptedPatient
    CONFLICTING_DATA: "conflicting-data", // solve conflicts in (existing) patient
  };

  const currentStatus = ref(status.SEARCH); // initially assume that decryptedPatient is valid

  /**
   * decryptedPatient should be a decrypted patient object or at
   * least a "fake" patient object containing these fields.
   * it's the import "source".
   */
  const decryptedPatient = ref({
    firstname: null,
    lastname: null,
    gender: null,
    birthdate: null,
    insurancenumber: null,
  });
  /**
   * patient selected as import "target"
   */
  const targetPatient = ref(null);

  const validateFirstname = computed(() => {
    return decryptedPatient.value?.firstname !== undefined &&
      decryptedPatient.value?.firstname !== "" &&
      decryptedPatient.value?.firstname !== null
      ? null
      : "Vorname muss ausgefüllt werden.";
  });

  const validateLastname = computed(() => {
    return decryptedPatient.value?.lastname !== undefined &&
      decryptedPatient.value?.lastname !== "" &&
      decryptedPatient.value?.lastname !== null
      ? null
      : "Nachname muss ausgefüllt werden.";
  });

  const validateBirthdate = computed(() => {
    return decryptedPatient.value?.birthdate !== undefined &&
      decryptedPatient.value?.birthdate !== "" &&
      decryptedPatient.value?.birthdate !== null
      ? null
      : "Geburtsdatum muss ausgefüllt werden.";
  });

  const validateGender = computed(() => {
    return decryptedPatient.value?.gender !== undefined &&
      decryptedPatient.value?.gender !== "" &&
      decryptedPatient.value?.gender !== null
      ? null
      : "Geschlecht muss ausgefüllt werden.";
  });

  /**
   * Minimum required fields for a patient search:
   * firstname, lastname, birthdate, gender
   */
  const isValidDecryptedPatient = computed(() => {
    return (
      validateFirstname.value === null &&
      validateLastname.value === null &&
      validateBirthdate.value === null &&
      validateGender.value === null
    );
  });

  const canTeleportButton = computed(() => {
    return !!document.querySelector("#UniversalPatientAssignmentSearchButton");
  });

  const birthdateFormatted = computed(() => {
    const d = new Date(decryptedPatient.value?.birthdate);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("de-DE", {dateStyle: "medium"});
  });

  watch(currentStatus, (newStatus) => {
    if (newStatus === status.SEARCH) {
      // always reset to normal search mode
      // when changing to search status
      resetLooseSearch(true);
      searchExistingPatients();
    }
  });

  watch(looseSearch, (newStatus) => {
    if (newStatus === true) {
      // always reset checkboxes in loose search
      // when switching to "loose search" mode
      resetLooseSearch();
    }
    searchExistingPatients();
  });

  watch(
    looseSearchIncludedFields,
    () => {
      searchExistingPatients();
    },
    {deep: true},
  );

  const searchExistingPatients = async () => {
    if (isVisible.value === false) {
      return;
    }

    isLoading.value = true;

    try {
      await search();
      isLoading.value = false;
    } catch (error) {
      console.error("Error occurred while searching for existing patients:", error);
      // If privacy password is expired
      await new Promise((resolve) => {
        const interval = setInterval(async () => {
          if (privacy.hasValidLocalStorage()) {
            await search();
            clearInterval(interval);
            resolve();
          }
        }, 1000);
      });
      isLoading.value = false;
    }
  };

  const search = async () => {
    if (decryptedPatient.value?.insurancenumber === null) {
      delete decryptedPatient.value.insurancenumber;
    }

    // trim all string attributes before searching
    decryptedPatient.value = Object.fromEntries(
      Object.entries(decryptedPatient.value).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ]),
    );

    const encryptedPatient = await privacy.encryptPatient(
      looseSearch.value
        ? {
            firstname: decryptedPatient.value?.firstname,
            lastname: decryptedPatient.value?.lastname,
            birthdate: looseSearchIncludedFields.value.birthdate ? decryptedPatient.value?.birthdate : null,
            gender: looseSearchIncludedFields.value.gender ? decryptedPatient.value?.gender : null,
            insurancenumber: looseSearchIncludedFields.value.insurancenumber
              ? decryptedPatient.value?.insurancenumber
              : null,
          }
        : decryptedPatient.value,
    );

    const response = await useAuthHttp(encryptedPatient).post(route("api.patients.search"));
    const patients = response?.data;

    const decryptedPatients = await Promise.all(patients.map(async (patient) => await privacy.decryptPatient(patient)));

    existingPatients.value = filterExistingPatients(
      looseSearch.value
        ? {
            firstname: (decryptedPatient.value?.firstname ?? "").slice(0, 3),
            lastname: (decryptedPatient.value?.lastname ?? "").slice(0, 3),
            birthdate: looseSearchIncludedFields.value.birthdate
              ? (decryptedPatient.value?.birthdate ?? "").slice(0, 4)
              : null,
            gender: looseSearchIncludedFields.value.gender ? decryptedPatient.value?.gender : null,
            insurancenumber: looseSearchIncludedFields.value.insurancenumber
              ? decryptedPatient.value?.insurancenumber
              : null,
          }
        : decryptedPatient.value,
      decryptedPatients,
      looseSearch.value
        ? [
            ...Object.keys(looseSearchIncludedFields.value).filter((key) => looseSearchIncludedFields.value[key]),
            "firstname",
            "lastname",
          ]
        : ["firstname", "lastname", "birthdate", "gender", "insurancenumber"],
      looseSearch.value,
    );

    if (props.suppressWithoutMatch && existingPatients.value.length === 0) {
      newPatient();
    }
    if (existingPatients.value.length > 0) {
      emit("hasMatch", existingPatients.value.length);
    }
  };

  function resetLooseSearch(switchToNormalSearch = false) {
    if (switchToNormalSearch) {
      looseSearch.value = false;
    }
    looseSearchIncludedFields.value = {
      name: true,
      birthdate: false,
      gender: false,
      insurancenumber: false,
    };
  }

  function is(value) {
    return currentStatus.value === value;
  }

  function setPatient(searchPatient) {
    isVisible.value = true;

    if (typeof searchPatient === "object") {
      decryptedPatient.value = searchPatient;
    }

    // always start with step "missing data"
    // when given decryptedPatient is invalid
    if (!isValidDecryptedPatient.value) {
      currentStatus.value = status.MISSING_DATA;
    }
    // otherwise start initial search
    else {
      searchExistingPatients();
    }
  }

  function clearPatient() {
    isVisible.value = false;

    looseSearch.value = false;
    decryptedPatient.value = null;
    existingPatients.value = [];
  }

  /**
   * Preselect an existing patient to check and solve data conflicts.
   * @param patient
   */
  function preselectPatient(patient) {
    targetPatient.value = patient;
    currentStatus.value = status.CONFLICTING_DATA;
  }

  /**
   * Select an existing patient (with already solved conflicts).
   */
  function confirmTargetPatient() {
    emit("existingPatientSelected", targetPatient.value);
  }

  function newPatient() {
    // always emit decryptedPatient, because the data could have been changed
    emit("newPatient", decryptedPatient.value);
  }

  defineExpose({
    setPatient,
    clearPatient,
  });
</script>

<template>
  <div>
    <section
      v-if="is(status.SEARCH)"
      class="flex flex-col"
      :class="{'min-h-138': looseSearch, 'min-h-96': !looseSearch}"
    >
      <slot
        name="search-info"
        :has-no-match="existingPatients.length === 0"
        :has-match="existingPatients.length > 0"
        :has-one-match="existingPatients.length === 1"
        :has-many-matches="existingPatients.length > 1"
      />

      <nav v-if="decryptedPatient" class="flex flex-col space-y-4 md:flex-row md:flex-wrap md:space-y-0 md:space-x-8">
        <div class="block flex-1 border-b-2 bg-gray-50 p-2">
          <div v-if="!looseSearch">
            <span class="inline-blick mr-1 text-gray-600">Suche nach</span>

            <span class="font-semibold after:content-[',_'] last:after:content-['']">
              <MedTooltip content="Vorname">
                <span>{{ decryptedPatient?.firstname }}</span>
              </MedTooltip>

              <MedTooltip content="Nachname">
                <span>{{ decryptedPatient?.lastname }}</span>
              </MedTooltip>
            </span>

            <span
              v-if="decryptedPatient?.birthdate_formatted ?? birthdateFormatted"
              class="after:content-[',_'] last:after:content-['']"
            >
              <MedTooltip content="Geburtsdatum">
                <span>{{ decryptedPatient?.birthdate_formatted ?? birthdateFormatted }}</span>
              </MedTooltip>
            </span>

            <span v-if="decryptedPatient?.gender" class="after:content-[',_'] last:after:content-['']">
              <MedTooltip content="Geschlecht">
                <span>{{ patientGender[decryptedPatient.gender] }}</span>
              </MedTooltip>
            </span>

            <span v-if="decryptedPatient?.insurancenumber">
              <MedTooltip content="Versichertennummer">
                <span>{{ decryptedPatient.insurancenumber }}</span>
              </MedTooltip>
            </span>
          </div>

          <div v-else class="flex flex-wrap space-x-4">
            <span class="inline-blick text-gray-600">Suche nach</span>

            <div class="flex font-semibold">
              <MedFormField disabled orientation="horizontal">
                <MedCheckbox
                  v-model="looseSearchIncludedFields.name"
                  :data-test="getTestId('universal-patient-assignment-loose-search-include-Fields-name-checkbox')"
                />

                <MedLabel>
                  <MedTooltip v-if="decryptedPatient?.firstname" content="Vorname / Nachname">
                    <span>
                      {{ decryptedPatient.firstname.substring(0, 3) }}...

                      {{ decryptedPatient.lastname.substring(0, 3) }}...
                    </span>
                  </MedTooltip>
                </MedLabel>
              </MedFormField>
            </div>

            <div v-if="decryptedPatient?.birthdate" class="flex">
              <MedFormField orientation="horizontal">
                <MedCheckbox
                  v-model="looseSearchIncludedFields.birthdate"
                  :data-test="getTestId('universal-patient-assignment-loose-search-include-Fields-birthdate-checkbox')"
                />

                <MedLabel>
                  <span class="text-base">
                    <MedTooltip content="Geburtsjahr">
                      {{ decryptedPatient.birthdate.substring(0, 4) }}
                    </MedTooltip>
                  </span>
                </MedLabel>
              </MedFormField>
            </div>

            <div v-if="decryptedPatient.gender" class="flex">
              <MedFormField orientation="horizontal">
                <MedCheckbox
                  v-model="looseSearchIncludedFields.gender"
                  :data-test="getTestId('universal-patient-assignment-loose-search-include-Fields-gender-checkbox')"
                />

                <MedLabel>
                  <span class="text-base">
                    <MedTooltip content="Geschlecht">
                      {{ patientGender[decryptedPatient.gender] }}
                    </MedTooltip>
                  </span>
                </MedLabel>
              </MedFormField>
            </div>

            <div v-if="decryptedPatient.insurancenumber" class="flex">
              <MedFormField orientation="horizontal">
                <MedCheckbox
                  v-model="looseSearchIncludedFields.insurancenumber"
                  :data-test="
                    getTestId('universal-patient-assignment-loose-search-include-Fields-insurancenumber-checkbox')
                  "
                />

                <MedLabel>
                  <span class="text-base">
                    <MedTooltip content="Versichertennummer">
                      {{ decryptedPatient.insurancenumber }}
                    </MedTooltip>
                  </span>
                </MedLabel>
              </MedFormField>
            </div>

            <MedLink
              as="button"
              variant="primary"
              icon="edit"
              :data-test="getTestId('universal-patient-assignment-change-search-parameter-button')"
              @click.prevent="currentStatus = status.EDIT_DATA"
            >
              Suchparamerter ändern
            </MedLink>
          </div>
        </div>

        <div class="flex items-center space-x-8">
          <MedLink
            as="button"
            variant="primary"
            :icon="looseSearch ? 'close' : 'search'"
            :data-test="getTestId('universal-patient-assignment-' + (looseSearch ? 'close' : 'search') + '-search')"
            :aria-disabled="isLoading"
            @click.prevent="looseSearch = !looseSearch"
          >
            {{ looseSearch ? "Suche zurücksetzen" : "Suche anpassen" }}
          </MedLink>
        </div>
      </nav>

      <div v-if="isLoading" class="flex flex-1 items-center justify-center">
        <MedSpinner size="2xl" :data-test="getTestId('universal-patient-assignment-spinner')" />
      </div>

      <div v-else-if="existingPatients.length === 0" class="flex flex-1 flex-col justify-between">
        <ComponentEmptyState class="mt-8" :test-id="getTestId('universal-patient-assignment')">
          <p>
            Bei der
            <span v-if="looseSearch">angepassten</span>
            Suche in Ihren bestehenden Patienten wurde kein
            <span v-if="!looseSearch">eindeutiger</span>
            Treffer gefunden.
          </p>

          <!-- TODO Find good fix for , foramtter -->
          <p v-if="looseSearch" class="mt-4">
            Sie können die
            <MedLink as="button" variant="primary" class="-mr-1" @click.prevent="currentStatus = status.EDIT_DATA">
              Suchparameter ändern
            </MedLink>
            , um bspw. Fehler in den importierten Quelldaten zu korrigieren.
          </p>

          <p v-else class="mt-4">
            Sie können die
            <MedLink as="button" variant="primary" class="-mr-1" @click.prevent="looseSearch = true">
              Suche anpassen
            </MedLink>
            , wenn Sie denken, dass der Patient mit abweichenden Angaben im MediCheck vorhanden sein müsste.
          </p>
        </ComponentEmptyState>

        <div class="mx-auto flex max-w-xl flex-wrap items-center space-x-8">
          <div class="mb-2 flex items-center space-x-2">
            <ComponentIcon class="text-2xl text-mcred">add</ComponentIcon>
            <div class="leading-tight">
              <div class="text-lg font-semibold text-gray-700">
                {{ decryptedPatient?.firstname }} {{ decryptedPatient?.lastname }}
              </div>

              <div class="text-xs">
                <span
                  v-if="decryptedPatient?.birthdate_formatted ?? birthdateFormatted"
                  class="after:content-[',_'] last:after:content-['']"
                >
                  {{ decryptedPatient?.birthdate_formatted ?? birthdateFormatted }}
                </span>

                <span v-if="decryptedPatient?.gender" class="after:content-[',_'] last:after:content-['']">
                  {{ patientGender[decryptedPatient.gender] }}
                </span>
                <span v-if="decryptedPatient?.insurancenumber">{{ decryptedPatient.insurancenumber }}</span>
              </div>
            </div>
          </div>

          <div class="flex flex-1 justify-end">
            <MedButton
              variant="primary"
              :data-test="getTestId('universal-patient-assignment-import-as-new-patient-button')"
              @click="newPatient"
            >
              Als neuen Patienten importieren
            </MedButton>
          </div>
        </div>
      </div>

      <ComponentSimpleExistingPatientSelect
        v-else-if="existingPatients.length > 0"
        class="mt-6"
        :existing-patients="existingPatients"
        :test-id="getTestId('transfer-import-dialog')"
        @patient-selected="preselectPatient"
      >
        <template #newItem>
          <div class="mx-auto flex max-w-xl flex-wrap items-center space-x-8">
            <div class="mb-2 flex items-center space-x-2">
              <ComponentIcon class="text-2xl text-mcred">add</ComponentIcon>

              <div class="leading-tight">
                <div class="text-lg font-semibold text-gray-700">
                  {{ decryptedPatient?.firstname }} {{ decryptedPatient?.lastname }}
                </div>

                <div class="text-xs">
                  <span
                    v-if="decryptedPatient?.birthdate_formatted ?? birthdateFormatted"
                    class="after:content-[',_'] last:after:content-['']"
                  >
                    {{ decryptedPatient?.birthdate_formatted ?? birthdateFormatted }}
                  </span>

                  <span v-if="decryptedPatient?.gender" class="after:content-[',_'] last:after:content-['']">
                    {{ patientGender[decryptedPatient.gender] }}
                  </span>
                  <span v-if="decryptedPatient?.insurancenumber">{{ decryptedPatient.insurancenumber }}</span>
                </div>
              </div>
            </div>

            <div class="flex flex-1 justify-end">
              <MedButton
                variant="primary"
                :data-test="getTestId('universal-patient-assignment-import-as-new-patient-button')"
                @click="newPatient"
              >
                Als neuen Patienten importieren
              </MedButton>
            </div>
          </div>
        </template>
      </ComponentSimpleExistingPatientSelect>
    </section>

    <section v-else-if="is(status.MISSING_DATA) || is(status.EDIT_DATA)">
      <template v-if="is(status.MISSING_DATA)">
        <slot
          name="missing-data-info"
          :has-no-match="existingPatients.length === 0"
          :has-match="existingPatients.length > 0"
          :has-one-match="existingPatients.length === 1"
          :has-many-matches="existingPatients.length > 1"
        >
          <ComponentAlert :test-id="getTestId('universal-patient-assignment-edit')">
            <strong class="font-semibold">
              Aus Ihrer Datenquelle (Warenwirtschaft, BMP o.ä.) wurden nicht alle Pflichtangaben übertragen.
            </strong>

            <p class="max-w-3xl">
              Für einen Datenimport in den MediCheck müssen mindestens Vorname, Nachname, Geburtsdatum sowie Geschlecht
              des Patienten vorhanden sein.
            </p>
          </ComponentAlert>

          <p>Sie können folgende fehlende Angabe(n) ergänzen, um mit dem Import fortzufahren.</p>
        </slot>
      </template>

      <template v-if="is(status.EDIT_DATA)">
        <slot
          name="edit-data-info"
          :has-no-match="existingPatients.length === 0"
          :has-match="existingPatients.length > 0"
          :has-one-match="existingPatients.length === 1"
          :has-many-matches="existingPatients.length > 1"
        >
          <p>Sie können Suchparameter ändern, um bspw. Fehler in den importierten Quelldaten zu korrigieren.</p>
        </slot>
      </template>

      <div class="mt-8 grid grid-cols-2 gap-8">
        <ComponentInput
          v-model="decryptedPatient.firstname"
          label="Vorname *"
          :validation="validateFirstname"
          :test-id="getTestId('universal-patient-assignment-edit-firstname')"
        />

        <ComponentInput
          v-model="decryptedPatient.lastname"
          label="Nachname *"
          :validation="validateLastname"
          :test-id="getTestId('universal-patient-assignment-edit-lastname')"
        />

        <ComponentInput
          v-model="decryptedPatient.birthdate"
          label="Geburtsdatum *"
          type="date"
          :hide-clear="true"
          :validation="validateBirthdate"
          :test-id="getTestId('universal-patient-assignment-edit-birthdate')"
        />

        <ComponentSelect
          v-model="decryptedPatient.gender"
          label="Geschlecht *"
          :options="patientGender"
          :validation="validateGender"
          :test-id="getTestId('universal-patient-assignment-edit-gender')"
        />

        <ComponentInput
          v-model="decryptedPatient.insurancenumber"
          label="Versichertennummer"
          :test-id="getTestId('universal-patient-assignment-edit-insurance-number')"
        />

        <div class="flex flex-row-reverse items-end justify-between">
          <Teleport v-if="canTeleportButton" to="#UniversalPatientAssignmentSearchButton">
            <template v-if="is(status.MISSING_DATA) || is(status.EDIT_DATA)">
              <MedButton
                variant="primary"
                :disabled="!isValidDecryptedPatient"
                :data-test="getTestId('universal-patient-assignment-edit-search-button')"
                @click="currentStatus = status.SEARCH"
              >
                Suchen
              </MedButton>
            </template>
          </Teleport>

          <template v-else>
            <MedButton
              :disabled="!isValidDecryptedPatient"
              :data-test="getTestId('universal-patient-assignment-edit-search-button')"
              @click="currentStatus = status.SEARCH"
            >
              Suchen
            </MedButton>
          </template>
          <span class="text-sm text-gray-500">* Pflichtangaben</span>
        </div>
      </div>

      <p v-if="is(status.MISSING_DATA)" class="mt-4 max-w-2xl">
        Bitte beachten Sie, dass diese ergänzten Daten
        <em class="font-semibold italic">nicht</em>
        zurück in Ihre Datenquelle übertragen werden. Der bessere Weg ist, wenn Sie die fehlenden Daten stattdessen
        direkt dort ergänzen und erneut den Import starten.
      </p>

      <p v-if="is(status.EDIT_DATA)" class="mt-4 max-w-2xl">
        Bitte beachten Sie, dass diese angepassten Daten
        <em class="font-semibold italic">nicht</em>
        zurück in Ihre Datenquelle übertragen werden. Der bessere Weg ist, wenn Sie die fehlerhaften Daten stattdessen
        direkt dort korrigieren und erneut den Import starten.
      </p>
    </section>

    <section v-else-if="is(status.CONFLICTING_DATA)">
      <ComponentSolveConflictingPatientData
        ref="refSolveConflictingPatientData"
        :source-patient="decryptedPatient"
        :target-patient="targetPatient"
        :test-id="getTestId('universal-patient-assignment')"
        @solved="confirmTargetPatient"
      />
    </section>
  </div>
</template>
